import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useAuth, hasRole, hasTag } from "../../context/AuthContext";
import { askMentor, trackEvent } from "../../lib/elearning";

const Panel = styled.aside`
  background: var(--nd-surface);
  border: 1px solid var(--nd-border-visible);
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 320px;

  @media (max-width: 991px) {
    position: fixed;
    inset: auto 0 0 0;
    transform: ${(p) => (p.open ? "translateY(0)" : "translateY(100%)")};
    transition: transform 300ms cubic-bezier(0.25, 0.1, 0.25, 1);
    border-radius: 12px 12px 0 0;
    max-height: 80vh;
    z-index: 90;
  }
`;

const Kicker = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-secondary);
  margin: 0;
`;

const Title = styled.h3`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--nd-text-display);
  margin: 0;
`;

const Hint = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 13px;
  color: var(--nd-text-secondary);
  margin: 0;
  line-height: 1.5;
`;

const Textarea = styled.textarea`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  color: var(--nd-text-primary);
  background: var(--nd-black);
  border: 1px solid var(--nd-border-visible);
  border-radius: 8px;
  padding: 12px;
  resize: vertical;
  min-height: 80px;
  outline: none;
  &:focus { border-color: var(--nd-text-primary); }
`;

const Submit = styled.button`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: var(--nd-text-display);
  color: var(--nd-black);
  border: none;
  border-radius: 999px;
  padding: 12px 20px;
  min-height: 44px;
  cursor: pointer;
  align-self: flex-start;
  transition: opacity 250ms cubic-bezier(0.25, 0.1, 0.25, 1);
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SignInBtn = styled(Submit)`
  background: transparent;
  color: var(--nd-text-primary);
  border: 1px solid var(--nd-border-visible);
`;

const GatePanel = styled.div`
  background: var(--nd-black);
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GateLine = styled.p`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--nd-text-primary);
  margin: 0;
`;

const GateMail = styled.a`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--nd-text-display);
  border-bottom: 1px solid var(--nd-border-visible);
  align-self: flex-start;
  text-decoration: none;
  padding-bottom: 2px;
  margin-top: 4px;
`;

const Answer = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: var(--nd-text-primary);
  background: var(--nd-black);
  border: 1px solid var(--nd-border);
  border-radius: 8px;
  padding: 16px;
  white-space: pre-wrap;
`;

const ErrorMsg = styled.p`
  font-family: 'Space Mono', monospace;
  font-size: 12px;
  color: var(--nd-accent);
  margin: 0;
`;

const CloseBtn = styled.button`
  display: none;
  @media (max-width: 991px) {
    display: inline-flex;
    font-family: 'Space Mono', monospace;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    background: transparent;
    color: var(--nd-text-secondary);
    border: 0;
    cursor: pointer;
    align-self: flex-end;
  }
`;

const QuotaHint = styled.span`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  color: var(--nd-text-secondary);
  align-self: center;
  margin-left: 8px;
`;

const AiMentor = ({ moduleId, moduleTitle, open, onClose }) => {
  const { user, role, tags, signIn } = useAuth();
  // Juniors are academy members by virtue of the trainee program.
  const hasAccess = hasRole(role, "admin") || hasTag(tags, "academy") || hasTag(tags, "junior");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const answerRef = useRef(null);

  useEffect(() => {
    if (answer && answerRef.current) {
      answerRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [answer]);

  const isExhausted = (err) => {
    if (!err) return false;
    const code = err.code || "";
    return code === "functions/resource-exhausted" || code.includes("resource-exhausted");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    if (!user) { signIn().catch(() => {}); return; }
    if (!hasAccess) return;
    if (remaining === 0) return;
    setBusy(true);
    setError(null);
    setAnswer(null);
    try {
      const res = await askMentor({ moduleId, question });
      setAnswer(res.answer || "(no reply)");
      if (typeof res.remaining === "number") setRemaining(res.remaining);
      trackEvent("mentor_question_asked", { module_id: moduleId, question_len: question.length });
    } catch (err) {
      if (isExhausted(err)) {
        setAnswer(err && err.message ? err.message : "Daily mentor limit reached. Back tomorrow.");
        setRemaining(0);
      } else {
        setError(err && err.message ? err.message : "Mentor unavailable. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel open={open}>
      <CloseBtn type="button" onClick={onClose}>Close</CloseBtn>
      <Kicker>AI MENTOR</Kicker>
      <Title>Ask about {moduleTitle}</Title>
      <Hint>Scoped to this module's curriculum. Replies are short, technical, and based on the lesson outline.</Hint>

      {user && !hasAccess ? (
        <GatePanel>
          <GateLine>
            The AI mentor is part of the Academy paid program — short, scoped
            replies trained on the module curriculum.
          </GateLine>
          <GateLine>
            You're signed in but don't have an Academy membership yet. Reach out
            and we'll set you up.
          </GateLine>
          <GateMail
            href={`mailto:hello@dioscuro.com?subject=Academy%20membership%20-%20${encodeURIComponent(moduleTitle || "")}&body=I'd%20like%20to%20enable%20the%20AI%20mentor%20on%20my%20account.`}
          >
            Request access →
          </GateMail>
        </GatePanel>
      ) : (
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Textarea
            placeholder="e.g. When should I use a StatefulSet over a Deployment?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={1000}
            disabled={busy}
            aria-label="Question for the AI mentor"
          />
          <div style={{ display: "flex", alignItems: "center" }}>
            {!user ? (
              <SignInBtn type="submit">Sign in to ask</SignInBtn>
            ) : (
              <Submit type="submit" disabled={busy || !question.trim() || remaining === 0}>
                {busy ? "Thinking…" : "Ask mentor"}
              </Submit>
            )}
            {typeof remaining === "number" && (
              <QuotaHint>{remaining} left today</QuotaHint>
            )}
          </div>
        </form>
      )}
      {error && <ErrorMsg>{error}</ErrorMsg>}
      {answer && <Answer ref={answerRef}>{answer}</Answer>}
    </Panel>
  );
};

export default AiMentor;
